import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Card, Button, Form, Spinner, Table, Badge } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const money = (value) =>
 new Intl.NumberFormat("en-IN", {
 style: "currency",
 currency: "INR",
 }).format(Number(value || 0));

const getItemKey = (item) =>
 [item.itemName || "", item.unit || "", item.description || ""]
 .map((value) => String(value).trim().toLowerCase())
 .filter(Boolean)
 .join("|");

// Resolve all available stages from a VendorWorkOrder's payment terms
const getAvailableStages = (vwo) => {
 if (!vwo) return [];
 const stages = new Set();
 const mode = vwo.paymentMode || "CUSTOM";

 // Global terms
 (vwo.paymentTerms || []).forEach((t) => {
 const label = mode === "STANDARD" ? t.stage : t.name;
 if (label?.trim()) stages.add(label.trim());
 });

 // Item-level terms
 (vwo.items || []).forEach((item) => {
 if (!item.paymentTerms?.length) return;
 const iMode = item.paymentMode || "CUSTOM";
 item.paymentTerms.forEach((t) => {
 const label = iMode === "STANDARD" ? t.stage : t.name;
 if (label?.trim()) stages.add(label.trim());
 });
 });

 return Array.from(stages);
};

// Get term % for a stage on a specific item (item-level → global fallback)
const getTermPercentage = (stageName, woItem, vwo) => {
 const norm = (s) => String(s || "").trim().toLowerCase();

 // Item-level
 if (woItem?.paymentTerms?.length) {
 const mode = woItem.paymentMode || "CUSTOM";
 const term = mode === "STANDARD"
 ? woItem.paymentTerms.find((t) => norm(t.stage) === norm(stageName))
 : woItem.paymentTerms.find((t) => norm(t.name) === norm(stageName));
 if (term) return Number(term.percentage || 0);
 }

 // Global fallback
 if (vwo?.paymentTerms?.length) {
 const mode = vwo.paymentMode || "CUSTOM";
 const term = mode === "STANDARD"
 ? vwo.paymentTerms.find((t) => norm(t.stage) === norm(stageName))
 : vwo.paymentTerms.find((t) => norm(t.name) === norm(stageName));
 if (term) return Number(term.percentage || 0);
 }

 return 0;
};

// Combined % for multiple stages on an item
const getTotalPercentage = (stageNames, woItem, vwo) => {
 return stageNames.reduce((sum, s) => sum + getTermPercentage(s, woItem, vwo), 0);
};

const VendorWorkOrderBillingAdd = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const vendorWorkOrderFromUrl = searchParams.get("vendorWorkOrder") || "";
 const workOrderFromUrl = searchParams.get("workOrder") || "";
 const projectFromUrl = searchParams.get("project") || "";

 const [vendorWorkOrders, setVendorWorkOrders] = useState([]);
 const [selectedVendorWorkOrderId, setSelectedVendorWorkOrderId] = useState(
 vendorWorkOrderFromUrl
 );
 const [selectedVendorWorkOrder, setSelectedVendorWorkOrder] = useState(null);
 const [existingBills, setExistingBills] = useState([]);
 const [approvedJMRQtyMap, setApprovedJMRQtyMap] = useState(new Map());
 const [items, setItems] = useState([]);
 const [selectedStages, setSelectedStages] = useState([]);
 const [invoiceNo, setInvoiceNo] = useState("");
 const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
 const [remarks, setRemarks] = useState("");
 const [billFile, setBillFile] = useState(null);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);

 useEffect(() => {
 const load = async () => {
 try {
 setLoading(true);
 const query = new URLSearchParams({ limit: "1000", status: "Approved" });
 if (workOrderFromUrl) query.set("workOrder", workOrderFromUrl);
 if (projectFromUrl) query.set("project", projectFromUrl);

 const res = await api.get(`/vendor-work-orders?${query.toString()}`);
 if (res.data.success) {
 const list = res.data.data || [];
 setVendorWorkOrders(list);
 const workOrderMatch = workOrderFromUrl
 ? list.find((wo) => String(wo.workOrder?._id || wo.workOrder || "") === String(workOrderFromUrl))
 : null;
 const projectMatch = projectFromUrl
 ? list.find((wo) => String(wo.project?._id || wo.project || "") === String(projectFromUrl))
 : null;
 const firstId =
 list.find((wo) => wo._id === vendorWorkOrderFromUrl)?._id ||
 workOrderMatch?._id ||
 projectMatch?._id ||
 list?.[0]?._id ||
 "";
 setSelectedVendorWorkOrderId(firstId);
 }
 } catch (error) {
 swal("Error", "Failed to load vendor work orders", "error");
 } finally {
 setLoading(false);
 }
 };

 load();
 }, [vendorWorkOrderFromUrl, workOrderFromUrl, projectFromUrl]);

 const selectedVendorWorkOrderMeta = useMemo(
 () => vendorWorkOrders.find((wo) => wo._id === selectedVendorWorkOrderId),
 [vendorWorkOrders, selectedVendorWorkOrderId]
 );

 const selectedProjectLabel = useMemo(() => {
 const matched = vendorWorkOrders.find((wo) => {
 const projectId = wo.project?._id || wo.project || wo.workOrder?.project?._id || wo.workOrder?.project || "";
 return String(projectId) === String(projectFromUrl);
 });
 return matched?.project?.projectName || matched?.project?.name || matched?.workOrder?.project?.projectName || matched?.workOrder?.project?.name || "";
 }, [vendorWorkOrders, projectFromUrl]);

 useEffect(() => {
 setSelectedVendorWorkOrder(selectedVendorWorkOrderMeta || null);
 setSelectedStages([]); // reset stages when VWO changes
 }, [selectedVendorWorkOrderMeta]);

 useEffect(() => {
 const loadExistingBills = async () => {
 if (!selectedVendorWorkOrderId) {
 setExistingBills([]);
 setApprovedJMRQtyMap(new Map());
 return;
 }

 try {
 const [billRes, jmrRes] = await Promise.all([
 api.get(`/vendor-work-order-billings?vendorWorkOrder=${selectedVendorWorkOrderId}`),
 api.get(`/vendor-jmrs?vendorWorkOrder=${selectedVendorWorkOrderId}&approvalStatus=Approved&limit=1000`),
 ]);

 if (billRes.data.success) setExistingBills(billRes.data.data || []);

 // Build map of total approved JMR qty per item
 if (jmrRes.data.success) {
 const qtyMap = new Map();
 (jmrRes.data.data || []).forEach((jmr) => {
 (jmr.items || []).forEach((item) => {
 const key = getItemKey(item);
 qtyMap.set(key, Number(qtyMap.get(key) || 0) + Number(item.measuredQuantity || 0));
 });
 });
 setApprovedJMRQtyMap(qtyMap);
 }
 } catch (error) {
 swal("Error", "Failed to load previous vendor bills", "error");
 }
 };

 loadExistingBills();
 }, [selectedVendorWorkOrderId]);

 const billedQuantityByItem = useMemo(() => {
 const billedMap = new Map();
 existingBills
 .filter((bill) => bill.status !== "Rejected")
 .forEach((bill) => {
 (bill.items || []).forEach((item) => {
 const itemKey = getItemKey(item);
 billedMap.set(
 itemKey,
 Number(billedMap.get(itemKey) || 0) + Number(item.quantity || 0)
 );
 });
 });
 return billedMap;
 }, [existingBills]);

 useEffect(() => {
 const nextItems = (selectedVendorWorkOrder?.items || [])
 .map((item) => {
 const originalQuantity = Number(item.quantity || 0);
 const alreadyBilledQuantity = Number(billedQuantityByItem.get(getItemKey(item)) || 0);
 const remainingQuantity = Math.max(originalQuantity - alreadyBilledQuantity, 0);

 const jmrApprovedQty = Number(approvedJMRQtyMap.get(getItemKey(item)) || 0);
 const suggestedQty = Math.min(jmrApprovedQty > 0 ? jmrApprovedQty : remainingQuantity, remainingQuantity);

 const woRate = Number(item.rate || 0);
 // Sum % across all selected stages
 const totalPercent = selectedStages.length > 0
 ? getTotalPercentage(selectedStages, item, selectedVendorWorkOrder)
 : 0;
 const computedRate = selectedStages.length > 0 && totalPercent > 0
 ? (woRate * totalPercent) / 100
 : woRate;

 return {
 ...item,
 originalQuantity,
 originalRate: woRate,
 totalPercent,
 alreadyBilledQuantity,
 remainingQuantity,
 jmrApprovedQty,
 quantity: suggestedQty,
 rate: computedRate,
 };
 })
 .filter((item) => item.remainingQuantity > 0);

 setItems(nextItems);
 }, [selectedVendorWorkOrderId, selectedVendorWorkOrder, billedQuantityByItem, approvedJMRQtyMap, selectedStages]);

 const taxes = useMemo(() => {
 const subTotal = items.reduce(
 (sum, item) => sum + Number(item.quantity || 0) * Number(item.rate || 0),
 0
 );
 const cgst = Number(selectedVendorWorkOrder?.cgst || 0);
 const sgst = Number(selectedVendorWorkOrder?.sgst || 0);
 const igst = Number(selectedVendorWorkOrder?.igst || 0);
 const cgstAmount = (subTotal * cgst) / 100;
 const sgstAmount = (subTotal * sgst) / 100;
 const igstAmount = (subTotal * igst) / 100;
 const taxAmount = cgstAmount + sgstAmount + igstAmount;

 return {
 subTotal,
 cgst,
 sgst,
 igst,
 cgstAmount,
 sgstAmount,
 igstAmount,
 taxAmount,
 totalAmount: subTotal + taxAmount,
 };
 }, [items, selectedVendorWorkOrder]);

 // Allowed billing amount = sum across ALL selected stages
 const allowedBillingAmount = useMemo(() => {
 if (!selectedStages.length || !selectedVendorWorkOrder) return null;
 const total = (selectedVendorWorkOrder.items || []).reduce((sum, item) => {
 const pct = getTotalPercentage(selectedStages, item, selectedVendorWorkOrder);
 return sum + (Number(item.quantity || 0) * Number(item.rate || 0) * pct) / 100;
 }, 0);
 return total;
 }, [selectedStages, selectedVendorWorkOrder]);

 const availableStages = useMemo(
 () => getAvailableStages(selectedVendorWorkOrder),
 [selectedVendorWorkOrder]
 );

 const toggleStage = (stage) => {
 setSelectedStages((prev) =>
 prev.includes(stage) ? prev.filter((s) => s !== stage) : [...prev, stage]
 );
 };

 const handleSubmit = async (e) => {
 e.preventDefault();

 if (!selectedVendorWorkOrderId) {
 swal("Error", "Select a vendor work order", "error");
 return;
 }

 if (!items.length || taxes.subTotal <= 0) {
 swal("Error", "Bill amount should be greater than zero", "error");
 return;
 }

 const invalidItem = items.find(
 (item) =>
 Number(item.quantity || 0) <= 0 ||
 Number(item.quantity || 0) > Number(item.remainingQuantity || 0) ||
 (!selectedStages.length && Number(item.rate || 0) > Number(item.originalRate || 0))
 );

 if (invalidItem) {
 swal("Error", `${invalidItem.itemName} — quantity exceeds remaining or rate is invalid`, "error");
 return;
 }

 if (selectedStages.length > 0) {
 const noTermItem = items.find((item) => !item.totalPercent || item.totalPercent === 0);
 if (noTermItem) {
 swal("Missing Payment Term", `No payment term for selected stages on item "${noTermItem.itemName}"`, "error");
 return;
 }
 }

 try {
 setSaving(true);
 const data = new FormData();
 data.append("vendorWorkOrder", selectedVendorWorkOrderId);
 data.append("invoiceNo", invoiceNo);
 data.append("invoiceDate", invoiceDate);
 data.append("remarks", remarks);
 data.append("items", JSON.stringify(items));
 if (selectedStages.length > 0) data.append("stages", JSON.stringify(selectedStages));
 if (billFile) data.append("billFile", billFile);

 const res = await api.post("/vendor-work-order-billings", data, {
 headers: { "Content-Type": "multipart/form-data" },
 });

 if (res.data.success) {
 swal("Success", "Vendor billing submitted", "success");
 navigate("/vendor-work-order-billing");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to save vendor bill", "error");
 } finally {
 setSaving(false);
 }
 };

 const updateItemQuantity = (index, value) => {
 setItems((prev) =>
 prev.map((item, itemIndex) => {
 if (itemIndex !== index) return item;
 const quantity = Math.min(
 Math.max(Number(value || 0), 0),
 Number(item.remainingQuantity || 0)
 );
 return { ...item, quantity };
 })
 );
 };

 const updateItemRate = (index, value) => {
 setItems((prev) =>
 prev.map((item, itemIndex) => {
 if (itemIndex !== index) return item;
 const rate = Math.min(
 Math.max(Number(value || 0), 0),
 Number(item.originalRate || 0)
 );
 return { ...item, rate };
 })
 );
 };

 const removeItem = (index) => {
 setItems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
 };

 if (loading) {
 return <div className="text-center py-5"><Spinner /></div>;
 }

 return (
 <Fragment>
 <PageTitle activeMenu="Vendor Billing" motherMenu="Commercial" />
 <Form onSubmit={handleSubmit}>
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Vendor Billing</Card.Title>
 <Button variant="light" size="sm" onClick={() => navigate(-1)}>
 Back
 </Button>
 </Card.Header>
 <Card.Body>
 <Row className="g-3 mb-4">
 <Col md={5}>
 <Form.Label>Vendor Work Order</Form.Label>
 <Form.Select
 value={selectedVendorWorkOrderId}
 onChange={(e) => setSelectedVendorWorkOrderId(e.target.value)}
 >
 <option value="">Select Vendor WO</option>
 {vendorWorkOrders
 .filter((wo) => {
 if (!projectFromUrl) return true;
 const projectId = wo.project?._id || wo.project || wo.workOrder?.project?._id || wo.workOrder?.project || "";
 return String(projectId) === String(projectFromUrl);
 })
 .map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNumber || wo.woNo || wo.title || wo._id}
 </option>
 ))}
 </Form.Select>
 {projectFromUrl && (
 <Form.Text className="text-muted">
 Project filter applied{selectedProjectLabel ? `: ${selectedProjectLabel}` : ""}.
 </Form.Text>
 )}
 </Col>
 <Col md={4}>
 <Form.Label>Vendor</Form.Label>
 <div className="form-control bg-light fw-bold d-flex align-items-center">
 {selectedVendorWorkOrder?.vendor?.name || "Vendor not loaded"}
 </div>
 </Col>
 <Col md={3}>
 <Form.Label>Status</Form.Label>
 <div>
 <Badge bg="info">Submit to Accounts</Badge>
 </div>
 </Col>

 {/* Stage Selection — Multi-select checkboxes */}
 <Col md={12}>
 <Form.Label className="fw-semibold">
 Billing Stage / Milestone
 <small className="text-muted fw-normal ms-1">(select one or more)</small>
 </Form.Label>
 {availableStages.length > 0 ? (
 <div className="border rounded p-3 d-flex flex-wrap gap-3">
 {availableStages.map((s) => {
 const pct = getTermPercentage(s, null, selectedVendorWorkOrder);
 return (
 <Form.Check
 key={s}
 type="checkbox"
 id={`stage-${s}`}
 label={
 <span>
 {s}
 {pct > 0 && (
 <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: 10 }}>
 {pct}%
 </Badge>
 )}
 </span>
 }
 checked={selectedStages.includes(s)}
 onChange={() => toggleStage(s)}
 />
 );
 })}
 </div>
 ) : (
 <div className="form-control bg-light text-muted">
 No payment terms set on this WO — rates will be entered manually
 </div>
 )}
 </Col>

 {/* Allowed Amount Preview */}
 {selectedStages.length > 0 && allowedBillingAmount !== null && (
 <Col md={12}>
 <div className="border rounded p-2 bg-light d-flex align-items-center gap-3">
 <div>
 <div className="small text-muted">Selected stages</div>
 <div className="fw-bold">{selectedStages.join(" + ")}</div>
 </div>
 <div className="ms-auto text-end">
 <div className="small text-muted">Allowed billing amount</div>
 <div className="fw-bold text-primary fs-6">{money(allowedBillingAmount)}</div>
 </div>
 </div>
 </Col>
 )}
 <Col md={4}>
 <Form.Label>Bill / Invoice No</Form.Label>
 <Form.Control value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
 </Col>
 <Col md={4}>
 <Form.Label>Bill Date</Form.Label>
 <Form.Control
 type="date"
 value={invoiceDate}
 onChange={(e) => setInvoiceDate(e.target.value)}
 />
 </Col>
 <Col md={4}>
 <Form.Label>Upload Vendor Bill</Form.Label>
 <Form.Control
 type="file"
 onChange={(e) => setBillFile(e.target.files?.[0] || null)}
 />
 </Col>
 <Col md={12}>
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 value={remarks}
 onChange={(e) => setRemarks(e.target.value)}
 />
 </Col>
 </Row>

 <div className="table-responsive">
 <Table bordered hover>
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Item</th>
 <th>Unit</th>
 <th>JMR Approved</th>
 <th>Qty</th>
 <th>Already Billed</th>
 <th>Remaining</th>
 {selectedStages.length > 0 && <th className="text-center">Total %</th>}
 <th>Rate</th>
 <th>Amount</th>
 <th></th>
 </tr>
 </thead>
 <tbody>
 {items.map((item, index) => (
 <tr key={item.itemName + index}>
 <td>{index + 1}</td>
 <td>
 <strong>{item.itemName}</strong>
 <div className="small text-muted">{item.description}</div>
 </td>
 <td>{item.unit || "-"}</td>
 <td>
 <span className="fw-bold text-info">{item.jmrApprovedQty || 0}</span>
 {item.jmrApprovedQty > 0 && (
 <div className="small text-muted">From JMR</div>
 )}
 </td>
 <td style={{ width: 120 }}>
 <Form.Control
 type="number"
 min="0"
 max={item.remainingQuantity}
 step="any"
 size="sm"
 value={item.quantity}
 onChange={(e) => updateItemQuantity(index, e.target.value)}
 />
 </td>
 <td>{item.alreadyBilledQuantity}</td>
 <td>
 <span className="fw-bold text-success">{item.remainingQuantity}</span>
 </td>
 {selectedStages.length > 0 && (
 <td className="text-center">
 <span className={`badge ${item.totalPercent > 0 ? "bg-success" : "bg-danger"}`}>
 {item.totalPercent > 0 ? `${item.totalPercent}%` : "No term"}
 </span>
 </td>
 )}
 <td style={{ width: 140 }}>
 {selectedStages.length > 0 && item.totalPercent > 0 ? (
 <>
 <div className="fw-bold text-success">{money(item.rate)}</div>
 <div className="small text-muted">WO: {money(item.originalRate)} × {item.totalPercent}%</div>
 </>
 ) : (
 <>
 <Form.Control
 type="number"
 min="0"
 max={item.originalRate}
 step="any"
 size="sm"
 value={item.rate}
 onChange={(e) => updateItemRate(index, e.target.value)}
 />
 <div className="small text-muted mt-1">
 Max: {money(item.originalRate)}
 </div>
 </>
 )}
 </td>
 <td>{money(Number(item.quantity || 0) * Number(item.rate || 0))}</td>
 <td className="text-center">
 <Button
 variant="outline-danger"
 size="sm"
 onClick={() => removeItem(index)}
 >
 <i className="fa fa-trash"></i>
 </Button>
 </td>
 </tr>
 ))}
 {items.length === 0 && (
 <tr>
 <td colSpan={selectedStages.length > 0 ? "11" : "10"} className="text-center text-muted py-4">
 No remaining quantity available for billing.
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>

 <Row className="justify-content-end">
 <Col md={4}>
 <div className="border rounded p-3">
 <div className="d-flex justify-content-between">
 <span>Sub Total</span>
 <strong>{money(taxes.subTotal)}</strong>
 </div>
 {taxes.cgst > 0 && (
 <div className="d-flex justify-content-between">
 <span>CGST ({taxes.cgst}%)</span>
 <strong>{money(taxes.cgstAmount)}</strong>
 </div>
 )}
 {taxes.sgst > 0 && (
 <div className="d-flex justify-content-between">
 <span>SGST ({taxes.sgst}%)</span>
 <strong>{money(taxes.sgstAmount)}</strong>
 </div>
 )}
 {taxes.igst > 0 && (
 <div className="d-flex justify-content-between">
 <span>IGST ({taxes.igst}%)</span>
 <strong>{money(taxes.igstAmount)}</strong>
 </div>
 )}
 <hr />
 <div className="d-flex justify-content-between text-primary">
 <span>Total</span>
 <strong>{money(taxes.totalAmount)}</strong>
 </div>
 </div>
 </Col>
 </Row>
 </Card.Body>
 <Card.Footer className="text-end bg-white">
 <Button type="submit" disabled={saving || !selectedVendorWorkOrderId}>
 {saving ? "Saving..." : "Save Vendor Bill"}
 </Button>
 </Card.Footer>
 </Card>
 </Form>
 </Fragment>
 );
};

export default VendorWorkOrderBillingAdd;
